import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";
import { tx, txv } from "../lib/tx";

/**
 * "Read while you wait" summary for the clickfix start screen (The Fake Browser
 * Fix). Three short slides (what/who, main risks, key terms) shown in the reader
 * while the 3D room downloads. Content only; the reader UI lives in
 * ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. fake verification page that asks for steps outside the browser   */
/* ------------------------------------------------------------------ */
function FakeCheckArt(): JSX.Element {
  const H = 296;
  const steps = ["Open the Run box on your keyboard", "Paste what the page copied for you", "Press Enter"];
  return (
    <Frame
      label={tx("Example: a fake verification page")}
      aria={tx("A browser page on a look-alike address with a human check that fails and then lists three steps to follow outside the browser, with a note that a real check stays in the browser")}
      height={H}
    >
      <rect x="16.5" y="34.5" width="327" height="24" rx="12" fill={C.panel2} stroke={C.line} />
      <circle cx="30" cy="46.5" r="3" fill={C.muted} fillOpacity="0.6" />
      <text x="40" y="50.5" fontSize="11" fontFamily={MONO} fill={C.soft}>
        cloudshield-verify.com
      </text>
      <rect x="276" y="39" width="60" height="15" rx="7.5" fill={C.amber} fillOpacity="0.12" stroke={C.amber} strokeOpacity="0.45" />
      <text x="306" y="50" fontSize="9.5" fontWeight="700" fill={C.amber} textAnchor="middle">
        {tx("look-alike")}
      </text>

      <rect x="16.5" y="68.5" width="327" height="46" rx="8" fill={C.panel2} stroke={C.line} />
      <rect x="29.5" y="80.5" width="23" height="23" rx="5" fill={C.code} stroke={C.muted} />
      <circle className="sr-anim-ring" cx="41" cy="92" r="11" fill="none" stroke={C.mint} strokeWidth="1.5" />
      <text x="66" y="96.5" fontSize="13" fontWeight="600" fill={C.text}>
        {tx("Verify you are human")}
      </text>
      <text x="330" y="96" fontSize="9.5" fill={C.muted} textAnchor="end">
        {tx("check")}
      </text>

      <rect x="16.5" y="124.5" width="327" height="108" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <rect x="16" y="130" width="3" height="97" rx="1.5" fill={C.red} />
      <text x="32" y="145" fontSize="12.5" fontWeight="700" fill={C.red}>
        {tx("Verification failed. To fix it:")}
      </text>
      {steps.map((s, i) => {
        const y = 168 + i * 24;
        return (
          <g key={s}>
            <circle cx="39" cy={y - 4} r="9" fill={C.panel2} stroke={C.line} />
            <text x="39" y={y - 0.5} fontSize="10.5" fontWeight="700" fill={C.red} textAnchor="middle">
              {i + 1}
            </text>
            <text x="56" y={y} fontSize="12" fill={C.text}>
              {txv(s)}
            </text>
          </g>
        );
      })}
      <rect className="sr-anim-blink" x="128" y="204" width="1.5" height="14" rx="0.75" fill={C.red} />

      <rect x="16.5" y="244.5" width="327" height="38" rx="8" fill={C.mint} fillOpacity="0.1" stroke={C.mint} strokeOpacity="0.4" />
      <AppChip x={24} y={251.5} />
      <text x="56" y="260" fontSize="12" fontWeight="600" fill={C.mint}>
        {tx("A real check stays in the browser")}
      </text>
      <text x="56" y="274" fontSize="10.5" fill={C.muted}>
        {tx("One click. Nothing to paste. Nothing to run.")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. three lures: command, alert with a phone number, remote control  */
/* ------------------------------------------------------------------ */
function LuresArt(): JSX.Element {
  const H = 298;
  return (
    <Frame
      label={tx("Three lures, one ask: act outside the browser")}
      aria={tx("Three cards: a command to paste and run, a security alert with a phone number to call, and a request to allow remote control of the laptop")}
      height={H}
    >
      {/* card 1: paste and run */}
      <rect x="16.5" y="34.5" width="327" height="72" rx="10" fill={C.panel2} stroke={C.line} />
      <rect x="16" y="42" width="3" height="57" rx="1.5" fill={C.amber} />
      <rect x="30" y="46" width="14" height="12" rx="2.5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <polyline points="33,50 35.5,52 33,54" fill="none" stroke={C.amber} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <text x="54" y="56" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Run this command to continue")}
      </text>
      <rect x="30" y="68" width="299" height="28" rx="6" fill={C.code} />
      <text x="40" y="86" fontSize="11" fontFamily={MONO} fill={C.soft}>
        <tspan fill={C.amber}>&gt;</tspan> {tx("fetch cloudshield-verify.com/fix | run")}
      </text>
      <rect className="sr-anim-blink" x="308" y="75" width="1.5" height="14" rx="0.75" fill={C.amber} />

      {/* card 2: alert with a phone number */}
      <rect x="16.5" y="114.5" width="327" height="72" rx="10" fill={C.panel2} stroke={C.line} />
      <rect x="16" y="122" width="3" height="57" rx="1.5" fill={C.red} />
      <path d="M37 126 l8 14 h-16 z" fill="none" stroke={C.red} strokeWidth="1.5" strokeLinejoin="round" />
      <line x1="37" y1="130.5" x2="37" y2="134.6" stroke={C.red} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="37" cy="137.3" r="0.9" fill={C.red} />
      <text x="54" y="137" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Your device is infected")}
      </text>
      <text x="30" y="156" fontSize="11" fill={C.muted}>
        {tx("Do not close this window. Call support now:")}
      </text>
      <rect className="sr-anim-pulse" x="30" y="162" width="150" height="18" rx="9" fill={C.red} fillOpacity="0.14" stroke={C.red} strokeOpacity="0.5" style={{ transformOrigin: "105px 171px" }} />
      <text x="105" y="174.5" fontSize="11" fontWeight="700" fontFamily={MONO} fill={C.red} textAnchor="middle">
        +1 800 555 0100
      </text>

      {/* card 3: remote control */}
      <rect x="16.5" y="194.5" width="327" height="72" rx="10" fill={C.panel2} stroke={C.line} />
      <rect x="16" y="202" width="3" height="57" rx="1.5" fill={C.mint} />
      <rect x="30" y="208" width="16" height="11" rx="2" fill="none" stroke={C.mint} strokeWidth="1.5" />
      <line x1="34" y1="223" x2="42" y2="223" stroke={C.mint} strokeWidth="1.5" strokeLinecap="round" />
      <text x="54" y="217" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Allow remote control of this laptop?")}
      </text>
      <text x="30" y="236" fontSize="11" fill={C.muted}>
        {tx("They will see your screen, files and saved passwords.")}
      </text>
      <rect x="30" y="243" width="64" height="18" rx="9" fill={C.panel} stroke={C.line} />
      <text x="62" y="255.5" fontSize="10.5" fontWeight="600" fill={C.soft} textAnchor="middle">
        {tx("Deny")}
      </text>
      <rect x="102" y="243" width="64" height="18" rx="9" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.5" />
      <circle className="sr-anim-ring" cx="134" cy="252" r="8" fill="none" stroke={C.mint} strokeWidth="1.3" />
      <text x="134" y="255.5" fontSize="10.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("Allow")}
      </text>

      <text x="180" y="287" fontSize="10.5" fill={C.muted} textAnchor="middle">
        {tx("Nothing on the laptop was ever broken.")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. how the trick runs + the safe path                               */
/* ------------------------------------------------------------------ */
function TrickFlowArt(): JSX.Element {
  const H = 296;
  const nodes = [
    { t: "Page", s: "fake check" },
    { t: "Clipboard", s: "hidden copy" },
    { t: "Run box", s: "you paste" },
    { t: "Attacker", s: "full access" },
  ];
  const safe = ["Stop. Close the tab", "Report it to IT or security", "Call the real IT service desk"];
  return (
    <Frame label={tx("How the trick runs, and the safe path")} aria={tx("A four-step flow from fake page to clipboard to Run box to attacker, then three safe steps: close the tab, report it, call the real IT service desk")} height={H}>
      {nodes.map((n, i) => {
        const x = 16 + i * 86.67;
        const cx = x + 34;
        const hot = i === 1;
        const last = i === nodes.length - 1;
        const col = last ? C.red : hot ? C.amber : C.mint;
        return (
          <g key={n.t}>
            <rect x={x + 0.5} y="40.5" width="67" height="54" rx="10" fill={C.panel2} stroke={C.line} />
            {hot ? <rect className="sr-anim-pulse" x={x + 0.5} y="40.5" width="67" height="54" rx="10" fill="none" stroke={C.amber} strokeOpacity="0.7" style={{ transformOrigin: `${cx}px 67px` }} /> : null}
            <circle cx={cx} cy="58" r="9" fill={col} fillOpacity="0.14" />
            <circle cx={cx} cy="58" r="3" fill={col} />
            <text x={cx} y="84" fontSize="11.5" fontWeight="700" fill={C.text} textAnchor="middle">
              {txv(n.t)}
            </text>
            <text x={cx} y="110" fontSize="10" fill={C.muted} textAnchor="middle">
              {txv(n.s)}
            </text>
            {last ? null : (
              <g>
                <line x1={x + 72} y1="67" x2={x + 82} y2="67" stroke={C.muted} strokeWidth="1.4" strokeLinecap="round" />
                <polyline points={`${x + 79.5},64 ${x + 82.5},67 ${x + 79.5},70`} fill="none" stroke={C.muted} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </g>
            )}
          </g>
        );
      })}
      <circle className="sr-anim-ring" cx="188.5" cy="58" r="9" fill="none" stroke={C.amber} strokeWidth="1.4" />

      <line x1="16" y1="126.5" x2="344" y2="126.5" stroke={C.line} />
      <text x="16" y="147" fontSize="12.5" fontWeight="700" fill={C.mint}>
        {tx("The safe path")}
      </text>

      {safe.map((s, i) => {
        const y = 158 + i * 42;
        return (
          <g key={s}>
            <rect x="16.5" y={y + 0.5} width="327" height="34" rx="9" fill={C.panel2} stroke={C.line} />
            <circle cx="36" cy={y + 17.5} r="10" fill={C.mint} fillOpacity="0.14" />
            <text x="36" y={y + 21.5} fontSize="11.5" fontWeight="700" fill={C.mint} textAnchor="middle">
              {i + 1}
            </text>
            <text x="56" y={y + 21.5} fontSize="12.5" fill={C.text}>
              {txv(s)}
            </text>
          </g>
        );
      })}
    </Frame>
  );
}

export const CLICKFIX_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "A real fix never asks you to leave the browser.",
    illustration: FakeCheckArt,
    caption: "A real check stays in the browser.",
    sections: [
      {
        h: "What this is about",
        body: "A page asks you to prove you are human, a pop-up claims your laptop is infected, and a caller offers to fix it. You decide at each step, then see the same moments from the attacker\u2019s side.",
      },
      {
        h: "Who it\u2019s for",
        body: "Everyone who browses the web, opens links from colleagues, or uses a work laptop. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "These pages work because you do the dangerous part yourself. Security tools let it happen, because you are allowed to open a box and press Enter. A page that sends you outside the browser is the warning sign, and spotting it is your call.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "Three ways a page pushes you out of the browser.",
    illustration: LuresArt,
    caption: "Different story each time. Same ask.",
    sections: [
      {
        h: "A fake check that wants a command run",
        body: "The page says verification failed and lists steps: open the Run box, paste, press Enter. It has already copied a command for you. Running it hands the attacker your laptop.",
      },
      {
        h: "A scare alert with a phone number",
        body: "A full-screen \u201Cvirus\u201D warning tells you not to close it and to call support. Real warnings never show a phone number. The number itself is the trap.",
      },
      {
        h: "A helpful caller who wants control",
        body: "The \u201Ctechnician\u201D asks you to install a tool or enter a code so they can take over the screen. That gives them your files and saved passwords.",
      },
      {
        h: "Nothing was ever broken",
        body: "There was no browser problem to fix. The only goal was to get you to act. Close the page, report it, and use the IT contact you already know.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: TrickFlowArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "Fake browser fix", body: "A page that invents a problem, then tells you to fix it yourself by running a command. Also called ClickFix." },
      { h: "Fake CAPTCHA", body: "A look-alike \u201Cverify you are human\u201D box. A real one is a click in the browser. This one ends with steps outside it." },
      { h: "Clipboard hijack", body: "The page quietly copies a command for you, so all you do is paste it and press Enter." },
      { h: "Run box and terminal", body: "Places where your computer runs typed commands. A web page has no reason to send you there." },
      { h: "Tech-support scam", body: "A fake alert or caller claiming your device is infected, to sell you a fix or get access." },
      { h: "Remote access tool", body: "Software that lets someone else see and control your screen. Only ever allow it for a person you reached yourself." },
    ],
  },
];
